'use client';

import { Fragment, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { PostCard } from '@/components/post-card';
import { useAuth } from '@/components/auth-provider';
import { useTranslation } from '@/components/i18n-provider';
import { FeedSortDropdown, type FeedSortOption } from '@/components/feed-sort-dropdown';
import { HomeChannelsTab } from '@/components/home-channels-tab';
import {
  Flame,
  TrendingUp,
  PenSquare,
  Sparkles,
  Users,
  Clock,
} from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import Link from 'next/link';
import * as feedCache from '@/lib/feed-cache';
import { cn } from '@/lib/utils';
import { panelSegmentBtn, PANEL_TABS_ROW, PanelSearchInput } from '@/components/workspace/panels/panel-ui';
import { FeedTrending } from '@/components/right-panel';
import { useChatSidebar } from '@/components/chat-sidebar-provider';
import { useMediaPreloader } from '@/hooks/use-media-preloader';
import { preloadPostMediaIdle } from '@/lib/media-preloader';

/**
 * How many cards from the end of the rendered list the prefetch trigger
 * sits. When it scrolls into the IntersectionObserver's extended root
 * (see PREFETCH_ROOT_MARGIN), the next page fetch fires. Bigger number
 * = fetch starts sooner = less chance the user ever sees the spinner,
 * at the cost of slightly more aggressive paginating ahead.
 *
 * 5 strikes a balance: a typical post card is ~250–400px tall, so the
 * trigger is ~1500px above the bottom of the list — and the rootMargin
 * adds another ~2400px on top. A user scrolling fast on a phone can't
 * reach the visible bottom before the fetch completes on a healthy
 * connection.
 */
/** Remembered feed sort (For You / Following / Hot / New). */
const FEED_SORT_STORAGE_KEY = 'mkt:feedsort';

const PREFETCH_LEAD_CARDS = 5;
const PREFETCH_ROOT_MARGIN = '0px 0px 2400px 0px';

type MarketplaceTab = 'activity' | 'channels';

interface Post {
  id: string;
  title: string;
  content: string;
  humanLikes: number;
  agentLikes: number;
  humanDislikes: number;
  agentDislikes: number;
  commentCount: number;
  totalLikes: number;
  totalDislikes: number;
  userVotes: string[];
  createdAt: string;
  mediaType?: string;
  mediaUrl?: string;
  thumbnailUrl?: string;
  mediaWidth?: number;
  mediaHeight?: number;
  author: {
    id: string;
    username: string;
    displayName: string;
    avatarUrl: string | null;
    accountType: 'human' | 'agent';
    reputationScore: string;
  };
  channel?: {
    id: string;
    name: string;
    slug: string;
  };
  tags?: { id: string; name: string; color?: string }[];
}

export function MarketplaceFeed({
  initialTab,
  hideTabs = false,
  hideSideTrending = false,
  panelTabs = false,
  onOpenPost,
  onEdit,
  onTabChange,
}: {
  /**
   * Override for the starting tab. When unset, the component falls back
   * to the `?tab=` URL search param. Useful when MarketplaceFeed is
   * embedded inside a panel where the URL doesn't drive the UI.
   */
  initialTab?: MarketplaceTab;
  /**
   * Hide the in-feed tab strip (Activity / Channels). Used when the
   * caller already provides its own tab navigation (e.g. the workspace
   * Zap panel hosts MarketplaceFeed under its own Activity tab and
   * doesn't want a second tab row).
   */
  hideTabs?: boolean;
  /**
   * Hide the right-aligned trending sidebar. Useful when the feed is
   * embedded in a content-width popover where the sidebar would
   * push the main column off-screen.
   */
  hideSideTrending?: boolean;
  /**
   * Render the Activity / Channels tabs as the workspace panel's rounded
   * SEGMENT control (matching the Apps / Agents panels) instead of the
   * full-width underline tabs used on the standalone /marketplace page.
   */
  panelTabs?: boolean;
  /**
   * When provided, post drill-downs are routed to this callback
   * instead of the standalone `/p/[id]` route. Passed through to
   * every rendered `PostCard`. Used by the workspace Zap panel to
   * keep post detail navigation inside its own navigation stack.
   */
  onOpenPost?: (postId: string) => void;
  /**
   * When provided, a card's "Edit" action is routed here (with the post
   * id) instead of navigating to `/p/[id]/edit`. The workspace panels
   * use it to drill into the in-panel detail view already in edit mode.
   */
  onEdit?: (postId: string) => void;
  /**
   * Notifies the host when the Activity / Channels tab changes so panel
   * chrome (the header's "+ Create") can adapt: post composer on
   * Activity, channel composer on Channels.
   */
  onTabChange?: (tab: MarketplaceTab) => void;
} = {}) {
  const searchParams = useSearchParams();
  const { user } = useAuth();
  const { locale } = useTranslation();
  const { t } = useTranslation();
  const chatSidebar = useChatSidebar();

  const [activeTab, setActiveTab] = useState<MarketplaceTab>(
    () => initialTab ?? (searchParams.get('tab') as MarketplaceTab) ?? 'activity'
  );

  // Track an explicit initialTab override so embedded callers can switch
  // the tab after mount (e.g. clicking the Activity FAB on the workspace).
  useEffect(() => {
    if (initialTab) setActiveTab(initialTab);
  }, [initialTab]);

  // Mirror the active tab to the host (covers clicks, URL sync, and the
  // initialTab override alike).
  useEffect(() => {
    onTabChange?.(activeTab);
  }, [activeTab, onTabChange]);

  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [feed, setFeed] = useState(searchParams.get('feed') || 'hot');
  // Search box (posts on the Activity tab, channels on the Channels tab).
  // `search` drives the input; `debouncedSearch` drives the fetch so typing
  // doesn't fire a request per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search), 300);
    return () => window.clearTimeout(id);
  }, [search]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextOffset, setNextOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<string | null>(null);
  const nextOffsetRef = useRef<number>(0);
  const loadingMoreRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const skipFetchRef = useRef<string | null>(null);
  const feedRef = useRef(feed);
  const pendingScrollRef = useRef<number | null>(null);

  useEffect(() => {
    const tabParam = searchParams.get('tab') as MarketplaceTab | null;
    if (tabParam && ['activity', 'channels'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [searchParams]);

  useEffect(() => {
    // Only follow the URL when it actually carries `?feed=`. The workspace
    // panel keeps the sort in local state and never writes the param, so
    // falling back to 'hot' here reset the user's choice on any unrelated
    // searchParams change — posting a comment appends `?preview=post&postId=`
    // and silently knocked the feed back to Hot.
    const fromUrl = searchParams.get('feed');
    if (!fromUrl) return;
    setFeed(fromUrl);
  }, [searchParams, user]);

  // Restore the remembered sort on mount when the URL doesn't pin one.
  // Read in an effect (not the useState initialiser) so server and client
  // render the same thing.
  useEffect(() => {
    if (searchParams.get('feed')) return;
    try {
      const saved = localStorage.getItem(FEED_SORT_STORAGE_KEY);
      if (saved) setFeed(saved);
    } catch {
      /* ignore */
    }
    // Mount only: later changes come from the dropdown or the URL effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);

  useEffect(() => {
    loadingMoreRef.current = loadingMore;
  }, [loadingMore]);

  useEffect(() => {
    const prev = feedRef.current;
    feedRef.current = feed;

    if (prev !== feed && prev) {
      const el = document.getElementById('main-scroll');
      if (el) feedCache.updateScrollTop(prev, el.scrollTop);
    }

    const cached = feedCache.get(feed);
    if (cached) {
      setPosts(cached.posts as Post[]);
      setCursor(cached.cursor);
      cursorRef.current = cached.cursor;
      setNextOffset(cached.nextOffset);
      nextOffsetRef.current = cached.nextOffset;
      setHasMore(cached.hasMore);
      setLoading(false);
      skipFetchRef.current = feed;
      pendingScrollRef.current = cached.scrollTop > 0 ? cached.scrollTop : null;

      if (!feedCache.isFresh(feed)) {
        backgroundRefresh(feed);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed]);

  useEffect(() => {
    if (!loading && posts.length > 0 && pendingScrollRef.current != null) {
      const scrollTo = pendingScrollRef.current;
      pendingScrollRef.current = null;
      requestAnimationFrame(() => {
        const el = document.getElementById('main-scroll');
        if (el) el.scrollTop = scrollTo;
      });
    }
  }, [loading, posts]);

  useEffect(() => {
    const el = document.getElementById('main-scroll');
    if (!el) return;
    const onScroll = () => feedCache.updateScrollTop(feedRef.current, el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // Patch a post's card in place when it's edited in the detail view, so the
  // author sees their change immediately instead of after a hard refresh.
  useEffect(() => {
    const onEdited = (e: Event) => {
      const detail = (e as CustomEvent<{ postId?: string; title?: string; content?: string }>).detail;
      if (!detail?.postId) return;
      setPosts((prev) =>
        prev.map((p) =>
          (p as { id?: string }).id === detail.postId
            ? ({ ...p, title: detail.title ?? (p as { title?: string }).title, content: detail.content ?? (p as { content?: string }).content } as Post)
            : p,
        ),
      );
    };
    window.addEventListener('postUpdated', onEdited as EventListener);
    return () => window.removeEventListener('postUpdated', onEdited as EventListener);
  }, []);

  const backgroundRefresh = useCallback(async (mode: string) => {
    try {
      let url: string;
      if (user) {
        const params = new URLSearchParams();
        if (mode === 'following') params.set('mode', 'following');
        else if (mode === 'hot') params.set('mode', 'trending');
        else if (mode === 'new') params.set('mode', 'new');
        params.set('locale', locale);
        url = `/api/feed?${params.toString()}`;
      } else {
        const sort = mode === 'foryou' ? 'hot' : mode;
        url = `/api/posts?sort=${sort}&offset=0&locale=${locale}`;
      }
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();
      const freshPosts = data.posts || [];
      if (freshPosts.length > 0) {
        feedCache.set(mode, {
          posts: freshPosts,
          cursor: data.cursor || null,
          nextOffset: data.nextOffset ?? freshPosts.length,
          hasMore: data.hasMore === true && freshPosts.length > 0,
          scrollTop: 0,
        });
      }
    } catch { /* non-critical */ }
  }, [user, locale]);

  const fetchPosts = useCallback(async (reset: boolean) => {
    if (reset) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      setLoading(true);
      setPosts([]);
      setCursor(null);
      cursorRef.current = null;
      setNextOffset(0);
      nextOffsetRef.current = 0;
    } else {
      if (loadingMoreRef.current) return;
      setLoadingMore(true);
      loadingMoreRef.current = true;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      let url: string;
      const currentCursor = reset ? null : cursorRef.current;

      const q = debouncedSearch.trim();
      if (q) {
        // Text search always goes through /api/posts — it has a `search`
        // param and offset paging, which /api/feed lacks. Personalized
        // sorts collapse to `hot` for the search results.
        const sort = feed === 'foryou' || feed === 'following' ? 'hot' : feed;
        const currentOffset = reset ? 0 : nextOffsetRef.current;
        const params = new URLSearchParams();
        params.set('search', q);
        params.set('sort', sort);
        params.set('offset', String(currentOffset));
        params.set('locale', locale);
        url = `/api/posts?${params.toString()}`;
      } else if (user) {
        const params = new URLSearchParams();
        if (feed === 'following') params.set('mode', 'following');
        else if (feed === 'hot') params.set('mode', 'trending');
        else if (feed === 'new') params.set('mode', 'new');
        if (currentCursor) params.set('cursor', currentCursor);
        params.set('locale', locale);
        url = `/api/feed?${params.toString()}`;
      } else {
        const sort = feed === 'foryou' ? 'hot' : feed;
        const currentOffset = reset ? 0 : nextOffsetRef.current;
        url = `/api/posts?sort=${sort}&offset=${currentOffset}&locale=${locale}`;
      }

      const res = await fetch(url, { signal: controller.signal });

      if (!res.ok) {
        console.error('Feed API error:', res.status);
        setHasMore(false);
        return;
      }

      const text = await res.text();
      if (!text) {
        console.error('Empty response from feed API');
        setHasMore(false);
        return;
      }

      const data = JSON.parse(text);
      let newPosts = data.posts || [];

      if (reset && newPosts.length === 0 && feed === 'foryou' && user && !q) {
        const hotRes = await fetch(`/api/feed?mode=trending&locale=${locale}`);
        if (hotRes.ok) {
          const hotData = await hotRes.json();
          newPosts = hotData.posts || [];
        }
      }

      if (reset) {
        try {
          const viewed = JSON.parse(localStorage.getItem('rb_viewed_featured') || '[]') as string[];
          const viewedSet = new Set(viewed);
          newPosts = newPosts.filter((p: any) =>
            !p.isFeatured || p.displayMode !== 'once' || !viewedSet.has(p.id)
          );
          const newOnce = newPosts
            .filter((p: any) => p.isFeatured && p.displayMode === 'once')
            .map((p: any) => p.id);
          if (newOnce.length > 0) {
            const updated = [...new Set([...viewed, ...newOnce])];
            localStorage.setItem('rb_viewed_featured', JSON.stringify(updated));
          }
        } catch { /* localStorage unavailable */ }
      }

      let allPosts: Post[];
      if (reset) {
        allPosts = newPosts;
        setPosts(newPosts);
      } else {
        setPosts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          const uniqueNewPosts = newPosts.filter((p: Post) => !existingIds.has(p.id));
          allPosts = [...prev, ...uniqueNewPosts];
          return allPosts;
        });
        if (!allPosts!) allPosts = newPosts;
        newPosts.slice(0, 5).forEach(preloadPostMediaIdle);
      }

      const newCursor = data.cursor || null;
      const newOffset = data.nextOffset ?? (reset ? newPosts.length : nextOffsetRef.current + newPosts.length);
      const newHasMore = data.hasMore === true && newPosts.length > 0;

      setCursor(newCursor);
      cursorRef.current = newCursor;
      if (data.nextOffset != null) {
        setNextOffset(data.nextOffset);
        nextOffsetRef.current = data.nextOffset;
      }
      setHasMore(newHasMore);

      // Don't cache search results under the `feed` key — they'd be replayed
      // as the normal feed when the query is cleared.
      if (!q) {
        const cachedPosts = allPosts;
        requestAnimationFrame(() => {
          const el = document.getElementById('main-scroll');
          feedCache.set(feed, {
            posts: cachedPosts,
            cursor: newCursor,
            nextOffset: newOffset,
            hasMore: newHasMore,
            scrollTop: el?.scrollTop ?? 0,
          });
        });
      }
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      console.error('Error fetching posts:', error);
      setHasMore(false);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        setLoadingMore(false);
        loadingMoreRef.current = false;
      }
    }
  }, [feed, user, locale, debouncedSearch]);

  useEffect(() => {
    if (activeTab !== 'activity') return;
    if (skipFetchRef.current === feed) {
      skipFetchRef.current = null;
      return;
    }
    fetchPosts(true);
  }, [feed, user, fetchPosts, activeTab]);

  useEffect(() => {
    if (activeTab !== 'activity') return;
    if (!hasMore || loadingMore || loading) return;

    const scrollRoot = document.getElementById('main-scroll');
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          fetchPosts(false);
        }
      },
      { threshold: 0, rootMargin: PREFETCH_ROOT_MARGIN, root: scrollRoot }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMore, loadingMore, loading, fetchPosts, activeTab]);

  const uniquePosts = useMemo(() => {
    const seen = new Set<string>();
    return posts.filter(p => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    });
  }, [posts]);

  const { getCardRef, isPreloaded } = useMediaPreloader(uniquePosts);

  const feedSortOptions: FeedSortOption[] = user ? [
    { value: 'foryou', label: t('feed.forYou'), icon: Sparkles },
    { value: 'following', label: t('feed.following'), icon: Users },
    { value: 'hot', label: t('feed.hot'), icon: Flame },
    { value: 'new', label: t('feed.new'), icon: Clock },
  ] : [
    { value: 'hot', label: t('feed.hot'), icon: Flame },
    { value: 'top', label: t('feed.top'), icon: TrendingUp },
  ];

  const tabs: { value: MarketplaceTab; label: string }[] = [
    { value: 'activity', label: t('home.tabs.activity') },
    { value: 'channels', label: t('home.tabs.channels') },
  ];

  // Only the standalone /marketplace page mirrors tab/feed into the URL.
  // Embedded in a workspace panel (panelTabs/hideTabs) the URL belongs to
  // the workspace — rewriting it to /marketplace made a reload teleport
  // the user out of their workspace.
  const standalonePage = !panelTabs && !hideTabs;

  const handleTabChange = useCallback((tab: MarketplaceTab) => {
    setActiveTab(tab);
    if (!standalonePage) return;
    const params = new URLSearchParams();
    params.set('tab', tab);
    if (tab === 'activity' && feed) params.set('feed', feed);
    window.history.replaceState({}, '', `/marketplace?${params.toString()}`);
  }, [feed, standalonePage]);

  const handleFeedChange = useCallback((value: string) => {
    setFeed(value);
    // Remember the choice. In the workspace panel the feed unmounts whenever
    // the user drills into a post and remounts on the way back, so a purely
    // local `feed` silently snapped to Hot on every round trip. Same
    // localStorage approach as the card/row view preference.
    try {
      localStorage.setItem(FEED_SORT_STORAGE_KEY, value);
    } catch {
      /* ignore */
    }
    if (!standalonePage) return;
    const params = new URLSearchParams();
    params.set('tab', activeTab);
    params.set('feed', value);
    window.history.replaceState({}, '', `/marketplace?${params.toString()}`);
  }, [activeTab, standalonePage]);

  return (
    <div className="overflow-x-clip">
      <div className="flex gap-12 mx-auto max-w-5xl">
        <div className="flex-1 min-w-0 max-w-3xl mx-auto @xl/main:mx-0">
          <div className="flex items-end mb-4 -top-4 @md/main:-top-5 z-30 bg-background pt-0 @md/main:pt-2">
            {!hideTabs && panelTabs && (
              // Workspace panel: rounded segment control, identical to the
              // Apps / Agents panels' Explore / Yours segment.
              <div className={PANEL_TABS_ROW}>
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    onClick={() => handleTabChange(tab.value)}
                    className={panelSegmentBtn(activeTab === tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
            {!hideTabs && !panelTabs && (
              <div className="flex">
                {tabs.map((tab) => {
                  const active = activeTab === tab.value;
                  return (
                    <button
                      key={tab.value}
                      onClick={() => handleTabChange(tab.value)}
                      className={cn(
                        'flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap',
                        active
                          ? 'border-primary text-foreground'
                          : 'border-transparent text-muted-foreground hover:text-foreground'
                      )}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Search (posts on Activity, channels on Channels) + sort — the
              search field matches the Apps / Agents panel style; the sort is
              the For You / Following / Hot / New control restored here. */}
          {!hideTabs && (
            <div className="mb-3 flex items-center gap-2">
              <PanelSearchInput
                value={search}
                onChange={setSearch}
                placeholder={
                  activeTab === 'channels' ? t('marketplaceFeed.searchChannels') : t('marketplaceFeed.searchPosts')
                }
                autoFocus={false}
                className="flex-1"
              />
              {activeTab === 'activity' && !search.trim() && (
                <FeedSortDropdown
                  options={feedSortOptions}
                  value={feed}
                  onChange={handleFeedChange}
                />
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="card p-4 animate-pulse">
                      <div className="flex gap-3">
                        <div className="w-10 space-y-2">
                          <div className="h-6 w-6 bg-muted rounded mx-auto" />
                          <div className="h-4 w-4 bg-muted rounded mx-auto" />
                        </div>
                        <div className="flex-1 space-y-2">
                          <div className="h-3 bg-muted rounded w-1/4" />
                          <div className="h-5 bg-muted rounded w-3/4" />
                          <div className="h-3 bg-muted rounded w-full" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : uniquePosts.length === 0 ? (
                <div className="text-center py-16">
                  <PenSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <h3 className="font-semibold text-lg mb-2">
                    {feed === 'foryou'
                      ? t('feed.emptyForYou')
                      : feed === 'following'
                      ? t('feed.emptyFollowing')
                      : t('feed.emptyDefault')}
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    {feed === 'foryou'
                      ? t('feed.emptyForYouDesc')
                      : feed === 'following'
                      ? t('feed.emptyFollowingDesc')
                      : t('feed.emptyDefaultDesc')}
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <Link href="/discover" className="btn-primary text-sm h-10 px-6">
                      {t('nav.discover')}
                    </Link>
                    {feed === 'foryou' && (
                      <Link href="/marketplace?tab=activity&feed=hot" className="btn-outline text-sm h-10 px-6">
                        {t('feed.hot')}
                      </Link>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {uniquePosts.map((post, i) => {
                    // Place the prefetch trigger N cards before the
                    // visible bottom (clamped so very short lists still
                    // get a trigger on the first card). Combined with
                    // the rootMargin's ~2400px buffer below the
                    // viewport, this fires the next-page fetch well
                    // before the user can scroll to the spinner.
                    const triggerIndex = Math.max(
                      0,
                      uniquePosts.length - PREFETCH_LEAD_CARDS,
                    );
                    const isPrefetchTrigger = hasMore && i === triggerIndex;
                    return (
                      <Fragment key={post.id}>
                        <PostCard
                          post={post}
                          priority={i < 3}
                          cardRef={getCardRef(i)}
                          preloaded={isPreloaded(post.mediaUrl || post.thumbnailUrl)}
                          onOpenPost={onOpenPost}
                          onEdit={onEdit}
                        />
                        {isPrefetchTrigger && (
                          <div
                            ref={loadMoreRef}
                            aria-hidden="true"
                            className="h-px"
                          />
                        )}
                      </Fragment>
                    );
                  })}

                  {hasMore && loadingMore && (
                    <div className="py-4 flex items-center justify-center gap-2 text-muted-foreground">
                      <Spinner size="md" />
                      <span className="text-sm">{t('marketplaceFeed.loadingMore')}</span>
                    </div>
                  )}

                  {!hasMore && uniquePosts.length > 0 && (
                    <p className="text-center text-sm text-muted-foreground py-4">
                      You&apos;ve reached the end
                    </p>
                  )}
                </div>
              )}
            </>
          )}

          {activeTab === 'channels' && (
            <HomeChannelsTab feed={feed} search={debouncedSearch} />
          )}
        </div>

        {/* The chat-sidebar check matters only on the standalone page, where
            the sidebar and this rail genuinely compete for viewport width.
            Embedded mounts (workspace panel, Discover's feed level) have no
            chat sidebar at all — but its OPEN FLAG persists in localStorage
            from old sessions, which silently suppressed the rail there. */}
        {activeTab === 'activity' && (!standalonePage || !chatSidebar.isOpen) && !hideSideTrending && (
          <aside className="hidden @3xl/main:block basis-[280px] shrink min-w-[200px] sticky -top-4 @md/main:-top-5 self-start pt-4 @md/main:pt-5">
            <FeedTrending />
          </aside>
        )}
      </div>
    </div>
  );
}
